import CreditCardIcon from '@mui/icons-material/CreditCard'
import EmailIcon from '@mui/icons-material/Email'
import GroupIcon from '@mui/icons-material/Group'
import HelpIcon from '@mui/icons-material/Help'
import LockIcon from '@mui/icons-material/Lock'
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import { List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import React, { useCallback } from 'react'
import { logoutRequest } from 'api/auth'
import { useNavigate } from 'react-router-dom'
import { TimelineTeamControlSelect } from '../../../features/Timeline/components/TimelineTeamControlSelect/TimelineTeamControlSelect'
import { routes } from '../../../routes/routes'
import { useActions } from '../../../store/hooks'
import { useAuthContext } from '../../context/AuthProvider'
import { useDrawersContext } from '../../context/ClientsDrawersProvider'
import { timeMeasureSystem } from '../../context/TimeMeasureSystem'
import { useFaq } from './useFaq'

export const ProfileList = ({ onClose }: { onClose: () => void }) => {
  const { logout } = useActions()

  const navigate = useNavigate()
  const theme = useTheme()
  const { clearUserData, user, isTrainee, isCaregiver, isIndividualTrainee } = useAuthContext()
  const {
    handleOpenInviteMember,
    handleOpenProfile,
    handleOpenChangePasswordDrawer,
    handleOpenContactUs,
    handleOpenMySubscription,
  } = useDrawersContext()

  const onClickInvite = useCallback(() => {
    onClose()
    handleOpenInviteMember()
  }, [handleOpenInviteMember, onClose])

  const onClickContactUs = useCallback(() => {
    onClose()
    handleOpenContactUs()
  }, [handleOpenContactUs, onClose])

  const onClickViewProfile = () => {
    handleOpenProfile(user?.id)
    onClose()
  }

  const onClickMySubscription = () => {
    handleOpenMySubscription()
    onClose()
  }

  const onClickChangePassword = () => {
    handleOpenChangePasswordDrawer()
    onClose()
  }

  const handleLogout = () => {
    // Flush engagement while the session token is still valid, then revoke.
    timeMeasureSystem.stopUserSession()
    void logoutRequest().finally(() => {
      logout()
      navigate(routes.signIn)
      clearUserData()
    })
  }

  const { onOpenFaq } = useFaq()

  const handleOpenFaq = () => {
    onClose()
    onOpenFaq()
  }

  const viewProfileText = <Typography variant="body1">View Profile</Typography>
  const mySubscriptionText = <Typography variant="body1">My Subscription</Typography>
  const changePassword = <Typography variant="body1">Change Password</Typography>
  const inviteTeamMember = <Typography variant="body1">Invite Team Member</Typography>
  const isInviteVisible = false
  const logoutText = <Typography variant="body1">Logout</Typography>
  const contactUsText = <Typography variant="body1">Contact Us</Typography>

  const myTeams = (
    <Typography variant="body1" color="grey.700" textAlign="center">
      My Teams
    </Typography>
  )
  return (
    <List
      component="nav"
      sx={{ p: 0, '& .MuiListItemIcon-root': { minWidth: 32, color: theme.palette.grey[500] } }}
    >
      <ListItemButton onClick={onClickViewProfile}>
        <ListItemIcon>
          <PersonOutlineOutlinedIcon />
        </ListItemIcon>
        <ListItemText primary={viewProfileText} />
      </ListItemButton>

      {/* Organization-based trainees have no subscription of their own — nothing to show them. */}
      {(isCaregiver || isIndividualTrainee) && (
        <ListItemButton onClick={onClickMySubscription}>
          <ListItemIcon>
            <CreditCardIcon />
          </ListItemIcon>
          <ListItemText primary={mySubscriptionText} />
        </ListItemButton>
      )}

      <ListItemButton onClick={onClickChangePassword}>
        <ListItemIcon>
          <LockIcon />
        </ListItemIcon>
        <ListItemText primary={changePassword} />
      </ListItemButton>

      {!isTrainee && isInviteVisible && (
        <ListItemButton onClick={onClickInvite}>
          <ListItemIcon>
            <GroupIcon />
          </ListItemIcon>
          <ListItemText primary={inviteTeamMember} />
        </ListItemButton>
      )}

      <ListItemButton onClick={onClickContactUs}>
        <ListItemIcon>
          <EmailIcon />
        </ListItemIcon>
        <ListItemText primary={contactUsText} />
      </ListItemButton>

      <ListItemButton onClick={handleOpenFaq}>
        <ListItemIcon>
          <HelpIcon />
        </ListItemIcon>
        <ListItemText primary={<Typography variant="body1">Help (FAQ)</Typography>} />
      </ListItemButton>

      <ListItemButton onClick={handleLogout}>
        <ListItemIcon>
          <LogoutOutlinedIcon />
        </ListItemIcon>
        <ListItemText primary={logoutText} />
      </ListItemButton>

      {isTrainee && (
        <>
          <ListItem sx={{ padding: 0 }}>
            <ListItemText primary={myTeams} />
          </ListItem>

          <ListItem>
            <TimelineTeamControlSelect onAfterChangeTeam={onClose} />
          </ListItem>
        </>
      )}
    </List>
  )
}
